import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { HOME } from "@/lib/content";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import {
  FileSearch,
  Workflow,
  ShieldCheck,
  Plug,
  Clock,
  AlertTriangle,
  FileWarning,
  ArrowRight,
  CheckCircle2,
  Scale,
  HardHat,
  Umbrella,
} from "lucide-react";
import { motion } from "framer-motion";

const iconMap: Record<string, React.ElementType> = {
  FileSearch,
  Workflow,
  ShieldCheck,
  Plug,
  Clock,
  AlertTriangle,
  FileWarning,
};

const industryIconMap: Record<string, React.ElementType> = {
  Legal: Scale,
  Construction: HardHat,
  Insurance: Umbrella,
};

export default function HomePage() {
  return (
    <Layout>
      <SEOHead
        title="ClauseArk — Contract review, redlining, and lifecycle for contract teams"
        description="ClauseArk is a contract review and lifecycle platform for contract teams in Legal, Construction, and Insurance. Playbook-based AI review, AI redlines, matters, intake forms, workflows, and a renewals dashboard."
      />

      {/* 1. Hero — navy background with grid */}
      <section className="relative bg-navy text-white pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 hero-grid pointer-events-none opacity-100" />
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold text-white/80 mb-6 tracking-wide uppercase">
                {HOME.hero.eyebrow}
              </div>
              <h1 className="text-display-xl mb-6 text-white">
                {HOME.hero.headline}
              </h1>
              <p className="text-body-lg text-slate-300 mb-10 max-w-2xl mx-auto">
                {HOME.hero.subhead}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link href={HOME.hero.primaryCta.path}>
                  <Button
                    size="lg"
                    className="w-full sm:w-auto h-12 px-8 bg-brand-gradient text-white border-0 hover:opacity-90 transition-opacity text-base font-semibold no-default-hover-elevate"
                  >
                    {HOME.hero.primaryCta.label}
                  </Button>
                </Link>
                <Link href={HOME.hero.secondaryCta.path}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto h-12 px-8 text-base font-semibold border-white/30 text-white bg-white/5 hover:bg-white/10 no-default-hover-elevate"
                  >
                    {HOME.hero.secondaryCta.label}
                  </Button>
                </Link>
              </div>
              {/*
                Tasteful tertiary text link below the buttons that points
                to the new "AI you actually own" positioning page
                (Task #584). Kept low-key (text + arrow, not a third
                button) so it doesn't compete with the primary "Book a
                demo" / "See pricing" CTAs above.
              */}
              {HOME.hero.tertiaryLink ? (
                <div className="mt-6 flex justify-center">
                  <Link
                    href={HOME.hero.tertiaryLink.path}
                    className="inline-flex items-center gap-1 text-sm font-medium text-cyan-200 hover:text-white underline-offset-4 hover:underline"
                    data-testid="home-hero-tertiary-link"
                  >
                    {HOME.hero.tertiaryLink.label}
                    <span aria-hidden="true">→</span>
                  </Link>
                </div>
              ) : null}
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. Pain Points — three cards on light gray */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-display text-navy">
              {HOME.painPoints.headline}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {HOME.painPoints.cards.map((card, i) => {
              const Icon = iconMap[card.icon] ?? AlertTriangle;
              return (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.45, delay: i * 0.1 }}
                  className="bg-white rounded-2xl p-8 border border-mist shadow-sm flex flex-col gap-4"
                >
                  <div className="h-11 w-11 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-h4 text-navy">{card.title}</h3>
                  <p className="text-body text-slate-600 leading-relaxed">
                    {card.body}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3. Solution Overview — 2x2 grid linking to Platform sub-pages */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-indigo font-semibold mb-3">
              Platform
            </div>
            <h2 className="text-display text-navy mb-5">
              {HOME.solutionOverview.headline}
            </h2>
            <p className="text-body-lg text-slate-600">
              {HOME.solutionOverview.sub}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {HOME.solutionOverview.cards.map((card, i) => {
              const Icon = iconMap[card.icon] ?? FileSearch;
              return (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.45, delay: i * 0.05 }}
                  className="bg-white rounded-2xl p-8 border border-mist hover:border-indigo/40 hover:shadow-lg transition-all flex flex-col gap-5"
                >
                  <div className="h-11 w-11 rounded-xl bg-cyan-50 text-cyan flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-h3 text-navy mb-3">{card.title}</h3>
                    <p className="text-body text-slate-600 leading-relaxed">
                      {card.body}
                    </p>
                  </div>
                  <Link
                    href={card.cta.path}
                    className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-indigo hover:text-navy transition-colors group"
                  >
                    {card.cta.label}
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. Product Pillars — what's actually built today */}
      <section className="py-24 md:py-32 bg-navy text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <h2 className="text-display text-white mb-5">
              {HOME.productPillars.headline}
            </h2>
            <p className="text-body-lg text-slate-300">
              {HOME.productPillars.sub}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {HOME.productPillars.items.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-white/5 border border-white/10 rounded-xl p-6 flex gap-4"
              >
                <CheckCircle2 className="h-5 w-5 text-cyan shrink-0 mt-1" />
                <div>
                  <h3 className="text-base font-semibold text-white mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    {item.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4a. Industries — three peer industry cards */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="home-industries">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-indigo font-semibold mb-3">
              {HOME.industries.eyebrow}
            </div>
            <h2 className="text-display text-navy mb-5">
              {HOME.industries.headline}
            </h2>
            <p className="text-body-lg text-slate-600">
              {HOME.industries.sub}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {HOME.industries.cards.map((card, i) => {
              const Icon = industryIconMap[card.industry] ?? Scale;
              return (
                <motion.div
                  key={card.industry}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  className="bg-white rounded-2xl p-8 border border-mist hover:border-indigo/40 hover:shadow-lg transition-all flex flex-col gap-5"
                  data-testid={`home-industry-card-${card.industry.toLowerCase()}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-indigo-50 text-indigo flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-h3 text-navy mb-3">{card.industry}</h3>
                    <p className="text-body text-slate-600 leading-relaxed">
                      {card.body}
                    </p>
                  </div>
                  <div className="mt-auto flex flex-col gap-2">
                    {card.links.map((link) => (
                      <Link
                        key={link.path}
                        href={link.path}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-indigo hover:text-navy transition-colors group"
                        data-testid={`home-industry-link-${link.path.split("/").pop()}`}
                      >
                        {link.label}
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-12 text-center">
            <Link href={HOME.industries.overviewCta.path}>
              <Button
                size="lg"
                variant="outline"
                className="h-11 px-6 text-sm font-semibold no-default-hover-elevate"
                data-testid="home-industries-overview-cta"
              >
                {HOME.industries.overviewCta.label}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 4b. Word add-in spotlight — dedicated section */}
      <section className="py-24 md:py-32" data-testid="home-word-addin-spotlight">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-7"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-indigo font-semibold mb-3">
                {HOME.wordAddinSpotlight.eyebrow}
              </div>
              <h2 className="text-display text-navy mb-5">
                {HOME.wordAddinSpotlight.headline}
              </h2>
              <p className="text-body-lg text-slate-600 mb-6">
                {HOME.wordAddinSpotlight.body}
              </p>
              <ul className="space-y-3 mb-8">
                {HOME.wordAddinSpotlight.bullets.map((bullet) => (
                  <li
                    key={bullet}
                    className="flex items-start gap-3 text-sm text-slate-700"
                  >
                    <CheckCircle2 className="h-4 w-4 text-indigo shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link href={HOME.wordAddinSpotlight.primaryCta.path}>
                  <Button
                    size="lg"
                    className="w-full sm:w-auto h-12 px-7 bg-brand-gradient text-white border-0 hover:opacity-90 text-base font-semibold no-default-hover-elevate"
                    data-testid="home-word-addin-primary-cta"
                  >
                    {HOME.wordAddinSpotlight.primaryCta.label}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <Link href={HOME.wordAddinSpotlight.secondaryCta.path}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto h-12 px-7 text-base font-semibold"
                  >
                    {HOME.wordAddinSpotlight.secondaryCta.label}
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Real screenshot of the ClauseArk task pane (light + dark variants) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <div className="mx-auto max-w-sm rounded-2xl border border-mist bg-white shadow-xl overflow-hidden">
                <div className="flex items-center gap-1.5 px-4 py-2 border-b border-mist bg-slate-50">
                  <span className="h-2.5 w-2.5 rounded-full bg-rose-300" />
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">
                    Word · Task pane
                  </span>
                </div>
                <picture>
                  <source
                    srcSet={`${import.meta.env.BASE_URL}word-addin/task-pane-dark.jpg`}
                    media="(prefers-color-scheme: dark)"
                  />
                  <img
                    src={`${import.meta.env.BASE_URL}word-addin/task-pane-light.jpg`}
                    alt="ClauseArk task pane in Microsoft Word reviewing an Acme MSA renewal — six findings ranked Critical to Low, an expanded High-risk indemnity finding, and a one-click Apply as tracked change action."
                    width={768}
                    height={830}
                    loading="lazy"
                    decoding="async"
                    className="block w-full h-auto"
                  />
                </picture>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4c. Review on the go — mobile/PWA callout */}
      <section
        className="py-20 md:py-28 bg-slate-50/60"
        data-testid="home-review-on-the-go"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-7"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-indigo font-semibold mb-3">
                {HOME.reviewOnTheGo.eyebrow}
              </div>
              <h2 className="text-display text-navy mb-5">
                {HOME.reviewOnTheGo.headline}
              </h2>
              <p className="text-body-lg text-slate-600 mb-6">
                {HOME.reviewOnTheGo.body}
              </p>
              <ul className="space-y-3">
                {HOME.reviewOnTheGo.bullets.map((bullet) => (
                  <li
                    key={bullet}
                    className="flex items-start gap-3 text-sm text-slate-700"
                  >
                    <CheckCircle2 className="h-4 w-4 text-indigo shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{bullet}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-5"
            >
              {/* Stylised phone mock — pure CSS so it ships in the page bundle. */}
              <div className="mx-auto max-w-[260px] rounded-[2.2rem] border-[10px] border-slate-900 bg-slate-900 shadow-2xl overflow-hidden">
                <div className="bg-white text-slate-900 aspect-[9/19] flex flex-col">
                  <div className="bg-navy text-white px-4 py-3 text-xs font-semibold tracking-wide flex items-center justify-between">
                    <span>My approvals</span>
                    <span className="text-[10px] opacity-70">3 pending</span>
                  </div>
                  <div className="flex-1 p-3 space-y-2">
                    <div className="rounded-lg border border-mist p-3 shadow-sm">
                      <div className="text-[11px] font-semibold text-navy">
                        Approve MSA — Northwind
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        Risk 62/100 · Due tomorrow
                      </div>
                      <div className="mt-2 grid grid-cols-3 gap-1 text-[10px] font-medium">
                        <div className="rounded bg-emerald-500 text-white text-center py-1">
                          Approve
                        </div>
                        <div className="rounded border border-rose-300 text-rose-600 text-center py-1">
                          Reject
                        </div>
                        <div className="rounded border border-slate-200 text-slate-500 text-center py-1">
                          Note
                        </div>
                      </div>
                    </div>
                    <div className="rounded-lg border border-mist p-3 shadow-sm opacity-90">
                      <div className="text-[11px] font-semibold text-navy">
                        Renewal — Acme SaaS
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        Risk 28/100 · Due Friday
                      </div>
                    </div>
                    <div className="rounded-lg border border-mist p-3 shadow-sm opacity-70">
                      <div className="text-[11px] font-semibold text-navy">
                        Vendor NDA — Globex
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        Risk 14/100 · Due Mon
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-mist px-4 py-2 text-[9px] text-slate-400 text-center">
                    ClauseArk · installed on this device
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 5. How It Works — four-step timeline */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-display text-navy">
              {HOME.howItWorks.headline}
            </h2>
          </div>
          <div className="max-w-3xl mx-auto relative">
            <div
              aria-hidden
              className="absolute left-5 top-2 bottom-2 w-px bg-gradient-to-b from-indigo via-cyan to-transparent hidden md:block"
            />
            <ol className="space-y-10 md:space-y-14">
              {HOME.howItWorks.steps.map((step, i) => (
                <motion.li
                  key={step.n}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  className="relative flex gap-5 md:gap-8"
                >
                  <div className="relative z-10 shrink-0 h-10 w-10 rounded-full bg-brand-gradient text-white font-bold text-sm flex items-center justify-center shadow-md">
                    {i + 1}
                  </div>
                  <div className="flex-1 pt-1">
                    <h3 className="text-h4 text-navy mb-2">{step.title}</h3>
                    <p className="text-body text-slate-600 leading-relaxed max-w-2xl">
                      {step.body}
                    </p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* 6. What we believe — values, not testimonials */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-display text-navy">
              {HOME.beliefs.headline}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {HOME.beliefs.items.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="bg-white rounded-2xl p-8 border border-mist shadow-sm flex flex-col gap-4"
              >
                <h3 className="text-h4 text-navy">{item.title}</h3>
                <p className="text-body text-slate-600 leading-relaxed">
                  {item.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Final CTA — indigo band */}
      <section className="py-24 bg-brand-gradient text-white relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-6 relative z-10 text-center max-w-3xl">
          <h2 className="text-display text-white mb-5">
            {HOME.finalCta.headline}
          </h2>
          <p className="text-body-lg text-white/90 mb-10">
            {HOME.finalCta.sub}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href={HOME.finalCta.primary.path}>
              <Button
                size="lg"
                className="w-full sm:w-auto h-12 px-8 bg-white text-indigo hover:bg-slate-100 transition-colors text-base font-semibold no-default-hover-elevate"
              >
                {HOME.finalCta.primary.label}
              </Button>
            </Link>
            <Link href={HOME.finalCta.secondary.path}>
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto h-12 px-8 border-white/40 bg-transparent text-white hover:bg-white/10 text-base font-semibold no-default-hover-elevate"
              >
                {HOME.finalCta.secondary.label}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
